import { Box, IconButton, Typography } from '@mui/material';
import React, {
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import EditIcon from '@mui/icons-material/Edit';
import { LabelInputSelect, NumberInput } from './BoxetteInputs';
import { getDecimalPrecision } from './getDecimalPrecision';

/**
 * EditableField component for displaying and editing form fields
 * @param {Object} props
 * @param {Object} props.formik - Formik form instance
 * @param {string} props.fieldKey - The field key in formik values
 * @param {string} props.translationKey - i18n translation key for the label
 * @param {Function} props.dynamicUnitName - Function to get display name for unit
 * @param {Object} [props.decimalsMask] - Decimal mask object (optional, deprecated)
 * @param {string} [props.displayStyle='editableText'] - Display style ('inputField' or 'editableText')
 * @param {Object} [props.mappingConfig] - Mapping configuration for custom precision
 */
export default function EditableField({
  formik,
  fieldKey,
  translationKey,
  dynamicUnitName,
  decimalsMask: _decimalsMask,
  displayStyle = 'editableText',
  mappingConfig,
}) {
  const [isEditMode, setIsEditMode] = useState(false);
  const { t } = useTranslation();

  // Safety check: ensure field exists in formik values
  if (!formik?.values?.[fieldKey]) {
    return null;
  }

  const fieldValue = formik.values[fieldKey];
  const unit = fieldValue?.unit || '';
  const value = fieldValue?.value ?? '';

  // If displayStyle is 'inputField', always show as input field
  if (displayStyle === 'inputField') {
    return (
      <Box sx={{
        display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 1,
      }}
      >
        <LabelInputSelect
          input={(
            <NumberInput
              label={t(translationKey)}
              id={`${fieldKey}.value`}
              name={`${fieldKey}.value`}
              value={value}
              onChange={formik.handleChange}
              decimalScale={getDecimalPrecision(fieldKey, unit, mappingConfig)}
              onBlur={formik.handleBlur}
            />
        )}
          select={(
            <Typography component="span" sx={{ p: 2 }}>
              {dynamicUnitName(unit)}
            </Typography>
        )}
        />
        <ErrorMessage
          touched={formik.touched[fieldKey]?.value}
          error={formik.errors[fieldKey]?.value}
        />
      </Box>
    );
  }

  // Default behavior for 'editableText' (existing behavior)
  return (
    <Box sx={{
      display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 1,
    }}
    >
      {!isEditMode ? (
        <>
          <Typography component="span">{`${t(translationKey)} : `}</Typography>
          <Typography component="span">
            {`${value} ${dynamicUnitName(unit)}`}
          </Typography>
          <IconButton onClick={() => setIsEditMode(true)} size="small">
            <EditIcon fontSize="inherit" />
          </IconButton>
        </>
      ) : (
        <LabelInputSelect
          input={(
            <NumberInput
              label={t(translationKey)}
              id={`${fieldKey}.value`}
              name={`${fieldKey}.value`}
              value={value}
              onChange={formik.handleChange}
              decimalScale={getDecimalPrecision(fieldKey, unit, mappingConfig)}
              onBlur={(e) => {
                formik.handleBlur(e);
                setIsEditMode(false);
              }}
            />
        )}
          select={(
            <Typography component="span" sx={{ p: 2 }}>
              {dynamicUnitName(unit)}
            </Typography>
        )}
        />
      )}
      <ErrorMessage
        touched={formik.touched[fieldKey]?.value}
        error={formik.errors[fieldKey]?.value}
      />
    </Box>
  );
}

function ErrorMessage({ touched, error }) {
  return touched && error && (
    <Typography component="span" style={{ fontSize: 13, color: 'red', marginTop: '8px' }}>
      {error}
    </Typography>
  );
}
